import { supabase } from '../supabase/config';
import { slugify } from '../utils/slugify';
import { recordConsent } from '../utils/consent';

/**
 * Service to handle authentication and tenant onboarding logic.
 */
export const authService = {
    /**
     * Registers a new tenant and an owner user.
     */
    async signup(email, password, corporateData) {
        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;

        const uid = data.user.id;
        const tenantId = uid;

        let baseSlug = slugify(corporateData.companyName || 'yeni-isletme');
        let finalSlug = baseSlug;
        let counter = 2;
        while (true) {
            const { data: existing } = await supabase.from('tenants').select('id').eq('slug', finalSlug).limit(1);
            if (!existing || existing.length === 0) break;
            finalSlug = `${baseSlug}-${counter}`;
            counter++;
        }

        // 1. Prepare Tenant Data
        const tenantRow = {
            id: tenantId,
            name: corporateData.companyName,
            slug: finalSlug,
            industry: corporateData.industry,
            website: corporateData.website || '',
            employee_count: corporateData.employeeCount || '',
            address: corporateData.address || '',
            city: corporateData.city || '',
            district: corporateData.district || '',
            phone: corporateData.phone || '',
            created_at: new Date().toISOString(),
            banner_url: null,
            owner_id: uid,
            plan: 'free',
            theme: {
                primary: '#6f4e37',
                mode: 'light',
                logoUrl: null
            },
            loyalty_rules: {
                welcomePoints: 150,
                purchasePointsEnabled: true,
                purchasePointsMultiplier: 0.1
            },
            loyalty_levels: [
                { id: '1', label: 'Silver', minPoints: 0, periodDays: 180, color: '#94A3B8' },
                { id: '2', label: 'Gold', minPoints: 500, periodDays: 180, color: '#FFD700' },
                { id: '3', label: 'Platinum', minPoints: 1000, periodDays: 180, color: '#9333EA' }
            ],
            visit_roles: [
                { id: 'v1', label: 'Yeni', minVisits: 1, periodDays: 30, color: '#F5F5DC' },
                { id: 'v2', label: 'Potansiyel', minVisits: 2, periodDays: 30, color: '#C68642' },
                { id: 'v3', label: 'Normal', minVisits: 4, periodDays: 30, color: '#8B4513' },
                { id: 'v4', label: 'Müdavim', minVisits: 8, periodDays: 30, color: '#3E2723' },
                { id: 'v5', label: 'Riskli', minVisits: 0, periodDays: 30, color: '#FF0000' }
            ]
        };


        // 2. Handle Logo Upload
        if (corporateData.logo) {
            const filePath = `logos/${tenantId}`;
            const { error: uploadError } = await supabase.storage
                .from('logos')
                .upload(filePath, corporateData.logo, { upsert: true });
            if (uploadError) throw uploadError;

            const { data: urlData } = supabase.storage
                .from('logos')
                .getPublicUrl(filePath);
            tenantRow.theme.logoUrl = urlData.publicUrl;
        }

        // Handle Banner Upload
        if (corporateData.banner) {
            const filePath = `banners/${tenantId}`;
            const { error: uploadError } = await supabase.storage
                .from('logos')
                .upload(filePath, corporateData.banner, { upsert: true });
            if (uploadError) throw uploadError;

            const { data: urlData } = supabase.storage
                .from('logos')
                .getPublicUrl(filePath);
            tenantRow.banner_url = urlData.publicUrl;
        }

        // 3. Save Tenant and User rows
        const { error: tenantError } = await supabase
            .from('tenants')
            .insert(tenantRow);
        if (tenantError) throw tenantError;

        // Record KVKK/privacy consent given during signup (best-effort).
        // Reaching signup requires the consent checkbox to be checked in SetupWizard.
        await recordConsent(supabase, 'tenants', tenantId, new Date().toISOString());

        const userDoc = {
            email,
            firstName: corporateData.adminInfo?.firstName || '',
            lastName: corporateData.adminInfo?.lastName || '',
            position: corporateData.adminInfo?.position || '',
            role: 'owner',
            tenantId,
            isSuperAdmin: email === 'admin@crmapp.com',
            createdAt: new Date()
        };

        const { error: userError } = await supabase
            .from('users')
            .insert({
                id: uid,
                email,
                first_name: userDoc.firstName,
                last_name: userDoc.lastName,
                position: userDoc.position,
                role: 'owner',
                tenant_id: tenantId,
                is_super_admin: userDoc.isSuperAdmin,
                created_at: new Date().toISOString()
            });
        if (userError) throw userError;

        // Return matching the existing pattern expected by AuthContext
        const userCredential = { user: data.user };
        return { userCredential, userDoc };
    },

    async login(email, password) {
        if (email === 'admin@crmapp.com') {
            throw new Error('System administrators must use the dedicated admin login portal.');
        }
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        return data;
    },

    async superAdminLogin(email, password, token) {
        const MASTER_TOKEN = import.meta.env.VITE_SUPER_ADMIN_TOKEN || 'PREMIUM-ADMIN-2026-TOKEN';

        if (token !== MASTER_TOKEN) {
            throw new Error('Invalid security token.');
        }

        if (email !== 'admin@crmapp.com') {
            throw new Error('Access denied. This login is restricted to system administrators.');
        }

        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        return data;
    },

    async resetPassword(email) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
    },

    async updatePassword(newPassword) {
        const { error } = await supabase.auth.updateUser({ password: newPassword });
        if (error) throw error;
    },

    async logout() {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
    },

    async getUserData(uid) {
        console.log('[Auth] getUserData called for uid:', uid);

        // Timeout wrapper - Supabase queries can hang if auth token is stale
        const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('getUserData timeout after 5s')), 5000)
        );

        try {
            const { data, error } = await Promise.race([
                supabase.from('users').select('*').eq('id', uid).maybeSingle(),
                timeoutPromise
            ]);

            console.log('[Auth] getUserData result:', { hasData: !!data, error: error?.message || error?.code || null });

            if (error) {
                if (error.code === 'PGRST116') return null; // No rows found
                throw error;
            }

            if (!data) return null;

            // Convert snake_case DB columns to camelCase to keep the rest of the app working
            return {
                email: data.email,
                firstName: data.first_name,
                lastName: data.last_name,
                position: data.position,
                phone: data.phone,
                role: data.role,
                tenantId: data.tenant_id,
                isSuperAdmin: data.is_super_admin,
                createdAt: data.created_at
            };
        } catch (err) {
            console.error('[Auth] getUserData failed:', err.message);
            // On timeout or error, try refreshing the session for next attempt
            try { await supabase.auth.refreshSession(); } catch {}
            return null;
        }
    }
};
